import React, { useState, useCallback, useRef, useEffect } from 'react';
import { StickyHeader } from '@hpstellar/core';
import loadable from '@loadable/component';

import AboveTheFoldOnlyServerRender from '../../../shared/components/above-the-fold-only-server-render';

import AddOnPrice from '../../../product/components/product-configurations/addon-price';
import withPageComponents from '../../../page/components/with-page-components';
import withConfigurations from '../../../product/components/product-configurations';
import { usePdpTabOrder } from '../pdp-layout';
import useScrollTarget from '../../../hooks/useScrollTarget';
import useProductPrice from '../../../hooks/useProductPrice';
import useAddToCart from '../../../hooks/useAddToCart';
import useCartState from '../../../hooks/useCartState';
import useTemplateTheme from '../../../hooks/useTemplateTheme';
import getStickyNavProductProps from './sticky-nav-product-props';
import { Helpers } from '../../../core/src/helpers';
import { formatMetricValue, getAddon } from '../../../metrics/metrics-helpers';
import { getProdDetailsTabWithGtm } from './analytics';
import { clickPdpAddToCartWithoutGtmAttributes } from '../../analytics';
import { NAV_SECTIONS } from '../constants';
import useLoadLazyComponent from '../../../shared/components/above-the-fold-only-server-render/useLoadLazyComponent';
import useLazyScroll from '../../../hooks/useLazyScroll';
import { ugcContainerID } from '../ugc';
import { scrollToSpecsAndConditionallyLockView } from '../../util';

const PDPFooter = loadable(() => Helpers.retryFunc(() => import('../pdp-footer')));

import './sticky-nav.less';

const { reviews, reviewsQA } = NAV_SECTIONS;
const MIN_HEIGHT = 56;

const FEATURE_LINK = {
    title: 'Features',
    hash: 'features',
};
const EMPTY_OBJECT = {};

const getAnchorLinks = (
    product,
    offers = [],
    pdpAccessories,
    pdpSimilarProductsLink,
    pdpSimilarProducts,
    exclusiveFeatures,
    pdpTechSpecs,
    productPrice,
    ugcAvailable,
    tabOrder,
) => {
    let { name, sku, social_gallery = true } = product || {};

    const reviewTabbedCondition = element => {
        return element && element.dataset.active === 'true';
    };

    // Build a map of all available anchor links by hash
    const anchorLinksMap = {
        pdpOverview: getProdDetailsTabWithGtm('Overview', 'pdpOverview', name, 'overview'),
        reviews: {
            title: reviews.navTitle || reviews.title,
            hash: reviews.hash,
            gtmValue: sku,
            gtmId: sku,
            gtmCategory: 'readReview',
            activeCondition: reviewTabbedCondition,
        },
        questions: {
            title: reviewsQA.navTitle || reviewsQA.title,
            hash: reviewsQA.hash,
            gtmValue: name,
            gtmId: 'questionsAndAnswers',
            gtmCategory: 'prodDetailsTab',
            activeCondition: reviewTabbedCondition,
        },
    };

    // Conditionally add sections based on availability
    if (pdpAccessories && pdpAccessories.products && Object.keys(pdpAccessories.products).length > 0) {
        anchorLinksMap.recommendedAccessories = getProdDetailsTabWithGtm(
            pdpAccessories.navTitle,
            'recommendedAccessories',
            name,
        );
    }

    if (exclusiveFeatures) {
        anchorLinksMap.features = getProdDetailsTabWithGtm(FEATURE_LINK.title, FEATURE_LINK.hash, name, 'features');
    }

    if (
        pdpTechSpecs &&
        ((pdpTechSpecs.technical_specifications && pdpTechSpecs.technical_specifications.length > 0) ||
            (pdpTechSpecs.bundleSpecs && pdpTechSpecs.bundleSpecs.length > 0) ||
            (pdpTechSpecs.datasheets && pdpTechSpecs.datasheets.length > 0))
    ) {
        anchorLinksMap.techSpecs = getProdDetailsTabWithGtm(
            'Specs',
            pdpTechSpecs.bundleSpecs && pdpTechSpecs.bundleSpecs.length > 0 ? 'bundlesTechSpecs' : 'techSpecs',
            name,
            'specs',
        );
    }

    if (offers.length > 0) {
        anchorLinksMap.specialOffers = getProdDetailsTabWithGtm('Offers', 'specialOffers', name, 'offers');
    }

    if (productPrice && productPrice.preOrder) {
        anchorLinksMap.faq = getProdDetailsTabWithGtm("Pre-order FAQ's", 'faq', name, 'faq');
    }

    if (product && social_gallery && ugcAvailable) {
        anchorLinksMap[ugcContainerID] = {
            title: '#MYHP',
            hash: ugcContainerID,
            gtmValue: sku,
            gtmId: 'bv-social',
            gtmCategory: 'prodDetailsTab',
        };
    }

    if (
        pdpSimilarProductsLink ||
        (pdpSimilarProducts && pdpSimilarProducts.products && pdpSimilarProducts.products.length > 0)
    ) {
        anchorLinksMap.similarProducts = getProdDetailsTabWithGtm('Similar', 'similarProducts', name, 'similar');
    }

    // If tabOrder is provided, build anchorLinks in that exact order
    // This ensures the nav order matches the actual DOM section order
    if (tabOrder && tabOrder.length > 0) {
        return tabOrder.map(hash => anchorLinksMap[hash]).filter(Boolean); // Remove undefined entries (sections not available)
    }

    // Fallback: return in default order if no tabOrder provided
    return Object.values(anchorLinksMap);
};

const StickyNAV = ({
    anchorLinks,
    productProps,
    onAdd,
    getCTOLink,
    stickyNavGtmActions,
    eolCTA,
    replacementCTA,
    theme,
}) => {
    const { bottomBarClassName, tabsListClasses } = theme || {};
    const { product, priceProps: price, gtmActions, purchaseBtnProps } = productProps;
    const { ctaText } = price || {};
    const { name, product_type } = product;
    //check purchase button props. if unavailable default to price ctaText
    const { children: purchaseBtnText = ctaText, disabled: disablePurchase } = purchaseBtnProps || {};
    //remove bundle flag for now as it is not in the comps and also takes up to much space and overflows. Ask Stellar Component team to take a look
    const { bundle, ...restPrice } = price || EMPTY_OBJECT;

    const prodObj = {
        name,
        price: restPrice,
    };
    const [loadLazyComponent] = useLoadLazyComponent();
    const loadReviews = useCallback(() => {
        loadLazyComponent(reviews.key);
    }, []);
    const [activeLink, setActiveLink] = useState('');
    const [scrollToTarget] = useLazyScroll();
    const inRange = useScrollTarget(
        'nav-boundary',
        undefined,
        lastKnownScrollPosition => {
            let activeHash;
            // Iterate through anchorLinks to find which section is currently in view
            // Since anchorLinks are now in DOM order (matching tabOrder),
            // the last section we've scrolled past is the active one
            anchorLinks.forEach(({ hash, activeCondition = () => true }) => {
                const anchorElem = document.getElementById(hash);
                if (
                    anchorElem &&
                    lastKnownScrollPosition >= anchorElem.offsetTop - 150 &&
                    activeCondition(anchorElem)
                ) {
                    activeHash = hash;
                }
            });
            setActiveLink(activeHash);
        },
        MIN_HEIGHT,
    );

    const hasScrolledToSpecs = useRef(null);
    const scrollCleanup = useRef(null);

    useEffect(() => {
        return () => {
            try {
                if (scrollCleanup && typeof scrollCleanup.current === 'function') {
                    scrollCleanup.current();
                    scrollCleanup.current = null;
                }
            } catch (e) {}
        };
    }, []);

    let addToCartGtmValues = gtmActions && gtmActions.get('addToCart');
    let customizeGtmValues = gtmActions && gtmActions.get('customize');
    const mainSkuGtm = { gtmObject: 'mainSKU' };

    try {
        addToCartGtmValues = {
            ...addToCartGtmValues,
            ...mainSkuGtm,
        };
    } catch (e) {}

    try {
        customizeGtmValues = {
            ...customizeGtmValues,
            ...mainSkuGtm,
        };
    } catch (e) {}

    let fullGtmActions = stickyNavGtmActions instanceof Map ? stickyNavGtmActions : new Map();
    fullGtmActions.set('addToCart', addToCartGtmValues);
    fullGtmActions.set('customize', customizeGtmValues);
    const customizeUrl = replacementCTA ? replacementCTA.to : price && !disablePurchase && getCTOLink(product, 1);
    const customizeText = replacementCTA ? replacementCTA.children : purchaseBtnText;
    const addText = eolCTA
        ? eolCTA.children
        : product_type !== 'CTO' || disablePurchase
          ? purchaseBtnText
          : 'Add to cart';
    /**
     * Temp wrapper div to reserve space for SSR
     */

    const { css } = useTemplateTheme();
    const { button: buttonProps } = useTemplateTheme(['button']);
    const customizeBtnProps = {
        ...buttonProps,
        disabled: replacementCTA && replacementCTA.disabled,
        to: customizeUrl,
    };
    const activeTabProps = {
        'data-gtm-pdptab': 'active',
    };

    return (
        <>
            <div style={{ minHeight: `${MIN_HEIGHT}px` }} onMouseEnter={loadReviews}>
                <StickyHeader
                    className="pdp-nav"
                    bottomBarClassName={bottomBarClassName}
                    product={prodObj}
                    anchorLinks={anchorLinks}
                    sticky={inRange}
                    upperBarVisible={inRange}
                    activeLink={activeLink}
                    onAdd={(eolCTA || (price && !price.hideAddToCart)) && onAdd}
                    addBtnDisabled={disablePurchase}
                    gtmActions={fullGtmActions}
                    tabsListClasses={tabsListClasses}
                    translations={{
                        addText,
                        customizeText,
                    }}
                    /* fake CTO for extra CA for EOL products */
                    productType={replacementCTA ? 'cto' : product_type?.toLowerCase()}
                    customizeBtnProps={customizeBtnProps}
                    addToCartButtonProps={buttonProps}
                    activeTabProps={activeTabProps}
                    onTabClick={idx => {
                        try {
                            if (scrollCleanup && typeof scrollCleanup.current === 'function') {
                                scrollCleanup.current();
                                scrollCleanup.current = null;
                            }
                        } catch (e) {}

                        try {
                            const { hash } = anchorLinks[idx];
                            if (['bundlesTechSpecs', 'techSpecs'].includes(hash)) {
                                let cleanupFunc = scrollToSpecsAndConditionallyLockView(
                                    hasScrolledToSpecs && hasScrolledToSpecs.current,
                                    {
                                        onComplete: () => {
                                            try {
                                                scrollCleanup.current = null;
                                                hasScrolledToSpecs.current = true;
                                            } catch (e) {}
                                        },
                                    },
                                );
                                scrollCleanup.current = cleanupFunc;
                            }
                        } catch (e) {}
                    }}
                />
            </div>
            <AboveTheFoldOnlyServerRender skip={true}>
                <PDPFooter hideShadowFooter={inRange} />
            </AboveTheFoldOnlyServerRender>
        </>
    );
};

const navContainer = ({
    productInitial,
    pdpSpecialOffers,
    pdpAccessories,
    pdpSimilarProductsLink,
    pdpSimilarProducts,
    pdpTechSpecs,
    exclusiveFeatures,
    device,
    trackCustomMetric,
    vanityUrl,
    ctoConfig,
    addOns,
    ugcAvailable,
}) => {
    const { tabOrder, navBar } = usePdpTabOrder();
    const { sku, eol } = productInitial || {};
    const addonProduct = addOns && getAddon(sku, addOns);
    const { prices } = useProductPrice([productInitial, ...(addonProduct ? [addonProduct] : [])], {
        withCTOConfigurations: true,
    });
    const { cartId } = useCartState();

    const { stickyNavGtmActions } = productInitial || {};
    const { addCTOToCart, addToCart, getCTOLink } = useAddToCart(false, { withCTOConfigurations: true });

    if (productInitial && device !== 'mobile') {
        const { sku, ctaReplacementURL, ctaAddToCart, ctaEol = 'VIEW ALTERNATE PRODUCT', ctaEolUrl } = productInitial;
        const { isNotDefaultColor, isNotDefaultConfig } = ctoConfig || {};
        const productPrice = prices[sku];
        const anchorLinks = getAnchorLinks(
            productInitial,
            pdpSpecialOffers,
            pdpAccessories,
            pdpSimilarProductsLink,
            pdpSimilarProducts,
            exclusiveFeatures,
            pdpTechSpecs,
            productPrice,
            ugcAvailable,
            tabOrder,
        );

        const isCTO = productInitial.product_type === 'CTO';
        const eolCTA = ctaEolUrl ? { children: ctaEol, to: ctaEolUrl } : null;
        const replacementCTA = ctaReplacementURL
            ? { children: ctaAddToCart, to: ctaReplacementURL }
            : eolCTA
              ? { children: 'NOT AVAILABLE', disabled: true, to: true }
              : null;
        const hideCTA =
            (eol && !eolCTA) ||
            (isCTO && (isNotDefaultColor || isNotDefaultConfig)) ||
            (productPrice && productPrice.hideAddToCart);
        return (
            anchorLinks.length > 0 && (
                <AddOnPrice sku={sku} price={productPrice}>
                    {({ totalPrice }) => {
                        let productProps = getStickyNavProductProps(productInitial, totalPrice, undefined, undefined, {
                            list: 'pdp',
                            cartId,
                            enableCtoAddToCart: true,
                            selectedAddonSku: addonProduct && addonProduct.sku,
                            prices,
                        });
                        if (Helpers.isGiftCard(productInitial)) {
                            productProps.disablePurchase = !!ctoConfig.error;
                            productProps.translations.buttons.purchaseBtnText = productPrice.ctaText;
                        }
                        return (
                            <StickyNAV
                                productProps={productProps}
                                anchorLinks={anchorLinks}
                                onAdd={
                                    hideCTA
                                        ? null
                                        : () => {
                                              if (isCTO) {
                                                  addCTOToCart(productInitial, 1);
                                              } else if (eolCTA) {
                                                  document.location.href = eolCTA.to;
                                              } else {
                                                  //TODO: temporary until Xsell refactor is complete
                                                  let mainBtn = document.getElementById('pdpAddToCartBtn');
                                                  if (mainBtn) {
                                                      // prevents additional addToCart event when clicking on the other button.
                                                      clickPdpAddToCartWithoutGtmAttributes(mainBtn);
                                                  } else {
                                                      addToCart(productInitial, 1);
                                                  }
                                              }
                                          }
                                }
                                getCTOLink={getCTOLink}
                                trackCustomMetric={trackCustomMetric}
                                stickyNavGtmActions={stickyNavGtmActions}
                                eolCTA={eolCTA}
                                replacementCTA={replacementCTA}
                                theme={navBar}
                            />
                        );
                    }}
                </AddOnPrice>
            )
        );
    }
    return (
        <AboveTheFoldOnlyServerRender skip={true} renderOn="load">
            <PDPFooter />
        </AboveTheFoldOnlyServerRender>
    );
};

export default withPageComponents(withConfigurations(navContainer), {
    components: [
        'productInitial',
        'pdpSpecialOffers',
        'pdpSimilarProductsLink',
        'pdpSimilarProducts',
        'pdpAccessories',
        'exclusiveFeatures',
        'pdpTechSpecs',
        'ugcAvailable',
    ],
    device: true,
    addOns: true,
});
